import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import SEO from "../Components/SEO";

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import AOS from 'aos';
import 'aos/dist/aos.css';

import Services from '../Components/Services';
import WhyChoose from '../Components/WhyChoose';
import Brands from '../Components/Brands';
import StatusSection from "../Components/StatusSection";
import FeaturedDoors from '../Components/FeaturedDoors';

const heroSlides = [
  { image: '/assets/prelaminated.webp', fallback: '/assets/prelaminated.png', type: 'Prelaminated' },
  { image: '/assets/Sunmake.webp', fallback: '/assets/Sunmake.png', type: 'Sunmica' },
  { image: '/assets/wpvc.webp', fallback: '/assets/wpvc.png', type: 'WPVC' },
  { image: '/assets/grewing.webp', fallback: '/assets/grewing.png', type: 'Grewing' },
  { image: '/assets/teak.webp', fallback: '/assets/teak.png', type: 'Teak' }
];

const Home = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const handleHeroRedirect = (doorType) => {
    navigate(`/doors?category=${doorType}`);
  };

  return (
    <>
      <SEO
        title="Sree Raghavendra Doors, Plywood & Hardware | Rajahmundry"
        description="Premium wooden doors, teak doors, waterproof WPC doors, Sunmica doors, marine plywood, and architectural hardware in Rajahmundry (Rajamahendravaram), AP. Visit our Morampudi showroom."
        canonicalUrl="/"
        keywords="doors shop in rajahmundry, doors and hardware in rajahmundry, plywood shop in rajahmundry, wooden doors in rajahmundry, teak wood doors in rajahmundry, wpc doors in rajahmundry, sunmica doors in rajahmundry, doors and plywood in east godavari, hardware store in rajamahendravaram"
        ogImage="https://raghavendradoorsandhardware.in/assets/prelaminated.png"
      />

      <div className="homepage-wrapper">
        <style>{`
          .hero-section {
            width: 100%;
            overflow: hidden;
            background: #000;
          }

          .hero-swiper {
            width: 100%;
            background: #000;
          }

          .hero-slide {
            cursor: pointer;
            overflow: hidden;
          }

          .hero-swiper,
          .hero-slide {
            height: 100%;
          }

          .hero-slide img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.5s ease;
            display: block;
          }

          @media (min-width: 1025px) {
            .hero-section {
              height: calc(100vh - 100px);
            }
            .hero-swiper {
              height: 100%;
            }
            .hero-slide {
              height: 100%;
            }
          }

          .hero-slide:hover img {
            transform: scale(1.02);
          }

          /* Hide default Swiper arrows */
          .hero-swiper .swiper-button-prev,
          .hero-swiper .swiper-button-next {
            display: none !important;
          }

          /* Simple clean arrows */
          .hero-swiper .custom-prev,
          .hero-swiper .custom-next {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            color: #fc7303;
            font-size: 45px;
            font-weight: 300;
            cursor: pointer;
            z-index: 30;
            transition: all 0.3s ease;
            user-select: none;
            line-height: 1;
          }

          .hero-swiper .custom-prev { left: 5px; }
          .hero-swiper .custom-next { right: 5px; }

          .hero-swiper .custom-prev:hover,
          .hero-swiper .custom-next:hover {
            color: #ffffff;
            transform: translateY(-50%) scale(1.15);
            text-shadow: 0 0 15px rgba(252, 115, 3, 0.7);
          }

          @media (max-width: 1024px) {
            .hero-swiper .custom-prev,
            .hero-swiper .custom-next {
              font-size: 30px;
              font-weight: 200;
            }
            .hero-swiper .custom-prev { left: 2px; }
            .hero-swiper .custom-next { right: 2px; }
            .hero-slide img {
              height: auto;
            }
          }

          .hero-swiper .swiper-pagination {
            bottom: 15px !important;
            z-index: 50;
          }

          .hero-swiper .swiper-pagination-bullet {
            width: 35px;
            height: 4px;
            border-radius: 4px;
            background: rgba(255, 255, 255, 0.35);
            opacity: 1;
            margin: 0 6px !important;
            cursor: pointer;
            transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
            transform-origin: center;
          }

          .hero-swiper .swiper-pagination-bullet:hover {
            background: rgba(252, 115, 3, 0.75);
            box-shadow: 0 4px 10px rgba(252, 115, 3, 0.3);
            transform: scale(1.28);
          }

          .hero-swiper .swiper-pagination-bullet-active {
            width: 50px;
            background: linear-gradient(90deg, #fc7303, #D4AF37) !important;
            box-shadow: 0 0 12px rgba(252, 115, 3, 0.4);
            transform: scale(1.05);
          }
        `}</style>

        <section className="hero-section">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            slidesPerView={1}
            loop={true}
            speed={1000}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            navigation={{
              nextEl: '.custom-next',
              prevEl: '.custom-prev',
            }}
            pagination={{ clickable: true }}
            grabCursor={true}
            allowTouchMove={true}
            className="hero-swiper"
          >
            {heroSlides.map((slide, index) => (
              <SwiperSlide key={index}>
                <div
                  className="hero-slide"
                  onClick={() => handleHeroRedirect(slide.type)}
                >
                  <picture>
                    <source srcSet={slide.image} type="image/webp" />
                    <img
                      src={slide.fallback}
                      alt={`${slide.type} Doors in Rajahmundry - Sree Raghavendra`}
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                    />
                  </picture>
                </div>
              </SwiperSlide>
            ))}

            <div className="custom-prev">❮</div>
            <div className="custom-next">❯</div>
          </Swiper>
        </section>

        <Services />
        <StatusSection />
        <FeaturedDoors />
        <WhyChoose />
        <Brands />
      </div>
    </>
  );
};

export default Home;
